import { Box, Flex, Spinner, useColorMode, useTheme } from "@chakra-ui/react";
import type { ActiveElement, ChartData, ChartEvent, ChartOptions, Chart as ChartType } from "chart.js";
import { ArcElement, Chart as ChartJS, Legend, Tooltip, TooltipItem } from "chart.js";
import { t } from "i18next";
import { useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import { ContractReportQueryDto, useListContractReportsQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { base64Encode } from "../../../common/utils/base64";
import { urls } from "../../../urls";
import {
  buildActiveFilters,
  buildAwardedFilters,
  buildExpiredFilters,
  buildTerminatedFilters,
} from "../../contracts/view-all/contractStatusFilters";

ChartJS.register(ArcElement, Tooltip, Legend);

export const ContractStatusChart = () => {
  const authState = useLoggedInWithOrgContextState();
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const theme = useTheme();

  const baseQ: Partial<ContractReportQueryDto> = useMemo(
    () => ({ owningOrganizationId: authState.selectedOrg.id }),
    [authState.selectedOrg.id]
  );

  const awardedQuery = useMemo(
    () => base64Encode(JSON.stringify(buildAwardedFilters({ base: baseQ, useLimitZero: true }))),
    [baseQ]
  );
  const activeQuery = useMemo(
    () => base64Encode(JSON.stringify(buildActiveFilters({ base: baseQ, useLimitZero: true }))),
    [baseQ]
  );
  const expiredQuery = useMemo(
    () => base64Encode(JSON.stringify(buildExpiredFilters({ base: baseQ, useLimitZero: true }))),
    [baseQ]
  );
  const terminatedQuery = useMemo(
    () => base64Encode(JSON.stringify(buildTerminatedFilters({ base: baseQ, useLimitZero: true }))),
    [baseQ]
  );

  const { data: awarded, isLoading: awardedLoading } = useListContractReportsQuery(
    { q: awardedQuery },
    { refetchOnMountOrArgChange: false, refetchOnFocus: false, refetchOnReconnect: false }
  );
  const { data: active, isLoading: activeLoading } = useListContractReportsQuery(
    { q: activeQuery },
    { refetchOnMountOrArgChange: false, refetchOnFocus: false, refetchOnReconnect: false }
  );
  const { data: expired, isLoading: expiredLoading } = useListContractReportsQuery(
    { q: expiredQuery },
    { refetchOnMountOrArgChange: false, refetchOnFocus: false, refetchOnReconnect: false }
  );
  const { data: terminated, isLoading: terminatedLoading } = useListContractReportsQuery(
    { q: terminatedQuery },
    { refetchOnMountOrArgChange: false, refetchOnFocus: false, refetchOnReconnect: false }
  );

  const isLoading = awardedLoading || activeLoading || expiredLoading || terminatedLoading;

  const textColor: string = colorMode === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)";
  const smPrimaryColor = colorMode === "dark" ? theme.colors.teal[200] : theme.colors.teal[500];
  const smSecondaryColor = colorMode === "dark" ? theme.colors.blue[100] : theme.colors.blue[400];

  const chartColors: string[] = [
    `${smSecondaryColor}`,
    `${smPrimaryColor}`,
    theme.colors.purple[400],
    theme.colors.red[400],
  ];

  const handleChartClick = (_event: ChartEvent, elements: ActiveElement[], _chart?: ChartType) => {
    if (elements.length === 0) return;
    const clickedElement = elements[0];
    if (clickedElement.index === 0) {
      const q = buildAwardedFilters({ base: baseQ });
      navigate(`${urls.contracts.viewAll}?q=${base64Encode(JSON.stringify(q))}`);
    } else if (clickedElement.index === 1) {
      const q = buildActiveFilters({ base: baseQ });
      navigate(`${urls.contracts.viewAll}?q=${base64Encode(JSON.stringify(q))}`);
    } else if (clickedElement.index === 2) {
      const q = buildExpiredFilters({ base: baseQ });
      navigate(`${urls.contracts.viewAll}?q=${base64Encode(JSON.stringify(q))}`);
    } else if (clickedElement.index === 3) {
      const q = buildTerminatedFilters({ base: baseQ });
      navigate(`${urls.contracts.viewAll}?q=${base64Encode(JSON.stringify(q))}`);
    }
  };

  const chartData: ChartData<"doughnut"> = useMemo(() => {
    const awardedCount = awarded?.count ?? 0;
    const activeCount = active?.count ?? 0;
    const expiredCount = expired?.count ?? 0;
    const terminatedCount = terminated?.count ?? 0;
    return {
      labels: [t("Awarded"), t("Active_plural"), t("Expired"), t("Terminated")],
      datasets: [
        {
          data: [awardedCount, activeCount, expiredCount, terminatedCount],
          backgroundColor: chartColors,
          borderColor: "#ffffff",
          borderWidth: 2,
          borderRadius: 6,
          hoverBackgroundColor: chartColors.map((c) => c + "CC"),
          hoverBorderColor: "#ffffff",
          hoverBorderWidth: 2,
        },
      ],
    };
  }, [awarded?.count, active?.count, expired?.count, terminated?.count, chartColors]);

  const chartOptions: ChartOptions<"doughnut"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: handleChartClick,
      onHover: (_event: ChartEvent, elements: ActiveElement[], chart: ChartType) => {
        const canvas = chart?.canvas as HTMLCanvasElement | undefined;
        if (!canvas) return;
        canvas.style.cursor = elements.length > 0 ? "pointer" : "default";
      },
      plugins: {
        legend: {
          display: true,
          position: "right",
          labels: {
            color: textColor,
            usePointStyle: true,
            pointStyle: "circle",
            boxWidth: 8,
            boxHeight: 8,
            padding: 12,
            font: {
              size: 12,
            },
          },
        },
        tooltip: {
          callbacks: {
            label: function (tooltipItem: TooltipItem<"doughnut">) {
              if (tooltipItem.raw && typeof tooltipItem.raw === "number") {
                return `${tooltipItem.label || ""}: ${tooltipItem.raw}`;
              }
              return "";
            },
          },
        },
      },
    }),
    [textColor]
  );

  return (
    <Box width="100%" mx="auto" pb="4">
      {isLoading ? (
        <Flex height="full" alignItems="center" justifyContent="center" pt="8">
          <Spinner color={smPrimaryColor} thickness="3px" speed="0.6s" size="lg" />
        </Flex>
      ) : (
        <Doughnut data={chartData} options={chartOptions} />
      )}
    </Box>
  );
};
